/**
 * Bill List — filterable bills with actions
 */

import React, { useMemo, useState } from 'react';
import { Check, Pencil, Trash2, Loader2, ChevronDown } from 'lucide-react';
import { useBills, useDeleteBill, useMarkBillPaid } from '../../hooks/useBudgetOverview';
import { useRegion } from '../../contexts/RegionContext';
import type { Bill } from '../../types/bill.types';
import { getBillDisplayName } from '../../types/bill.types';
import { PrimaryCategoryChip } from '../../components/categories/PrimaryCategoryChip';

interface BillListProps {
  onEdit: (bill: Bill) => void;
}

const ALL_CATEGORIES = 'all';
const UNCATEGORISED = '__uncategorised__';

export function BillList({ onEdit }: BillListProps): React.ReactElement {
  const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES);
  const { data: bills = [], isLoading } = useBills();
  const deleteBill = useDeleteBill();
  const markPaid = useMarkBillPaid();
  const { formatCurrency, formatDate } = useRegion();

  const categoryOptions = useMemo(() => {
    const names = new Set<string>();
    let hasUncategorised = false;
    for (const bill of bills) {
      if (bill.primaryCategoryName?.trim()) {
        names.add(bill.primaryCategoryName.trim());
      } else {
        hasUncategorised = true;
      }
    }
    return {
      names: [...names].sort((a, b) => a.localeCompare(b)),
      hasUncategorised,
    };
  }, [bills]);

  const filteredBills = useMemo(() => {
    if (categoryFilter === ALL_CATEGORIES) return bills;
    if (categoryFilter === UNCATEGORISED) {
      return bills.filter((b) => !b.primaryCategoryName?.trim());
    }
    return bills.filter((b) => b.primaryCategoryName?.trim() === categoryFilter);
  }, [bills, categoryFilter]);

  const filteredTotal = useMemo(
    () => filteredBills.reduce((sum, bill) => sum + bill.amount, 0),
    [filteredBills]
  );

  const handleDelete = async (bill: Bill) => {
    if (!window.confirm(`Delete "${getBillDisplayName(bill)}"? This cannot be undone.`)) return;
    try {
      await deleteBill.mutateAsync(bill.id);
    } catch (e) {
      console.error('Failed to delete bill:', e);
    }
  };

  const handleMarkPaid = async (bill: Bill) => {
    try {
      await markPaid.mutateAsync(bill.id);
    } catch (e) {
      console.error('Failed to mark bill paid:', e);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" aria-label="Loading bills" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Bills</h2>
          <div className="relative">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 appearance-none cursor-pointer"
              aria-label="Filter bills by category"
            >
              <option value={ALL_CATEGORIES}>All categories</option>
              {categoryOptions.names.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
              {categoryOptions.hasUncategorised && (
                <option value={UNCATEGORISED}>Uncategorised</option>
              )}
            </select>
            <ChevronDown
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          {filteredBills.length} {filteredBills.length === 1 ? 'bill' : 'bills'}
          {filteredBills.length > 0 && (
            <span> · {formatCurrency(filteredTotal)}</span>
          )}
        </p>
      </div>

      {filteredBills.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">No bills found. Add a bill to start tracking.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {filteredBills.map((bill) => {
            const label = getBillDisplayName(bill);
            return (
            <div key={bill.id} className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{label}</h3>
                    {bill.primaryCategoryName && (
                      <PrimaryCategoryChip
                        name={bill.primaryCategoryName}
                        color={bill.primaryCategoryColor}
                      />
                    )}
                    {bill.autopay && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                        Auto-pay
                      </span>
                    )}
                  </div>
                  {label !== bill.name && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate" title={bill.name}>
                      Original: {bill.name}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400">
                    <span>{formatDate(bill.dueDate)}</span>
                    <span>{bill.relativeTiming}</span>
                    <span className="capitalize">{bill.frequency.replace('_', ' ')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(bill.amount)}</span>
                  <div className="flex items-center gap-1">
                    {bill.status !== 'paid' && !bill.autopay && (
                      <button
                        type="button"
                        onClick={() => handleMarkPaid(bill)}
                        disabled={markPaid.isPending}
                        className="p-2 text-green-600 hover:text-green-700 dark:text-green-400 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
                        aria-label={`Mark ${label} as paid`}
                        title="Mark paid"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onEdit(bill)}
                      className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      aria-label={`Edit ${label}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(bill)}
                      disabled={deleteBill.isPending}
                      className="p-2 text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                      aria-label={`Delete ${label}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
